import {DOCUMENT_FIELDS_PATH, type EntityOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import {clearProjectItemFieldValueMutation as queryId} from '@github-ui/server-persisted-queries'
import type {ClearProjectItemFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/clearProjectItemFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {
  applyProjectItemFieldValue,
  projectItemFieldValueFromResponse,
  removeProjectItemFieldValue,
} from './project-item-field-value-optimistic'

type RemoveProjectItemFieldValueOperation = Extract<EntityOperation, {type: 'removeEntityFieldItem'}>
type MutationContext = {relayUpdate?: Disposable}

function clearRelayFieldValue(store: RecordSourceProxy, itemId: string, fieldName: string): void {
  store.get(itemId)?.setValue(null, 'fieldValueByName', {name: fieldName})
}

async function clearProjectItemFieldValueRequest({
  owner,
  repo,
  issueNumber,
  operation,
}: {
  owner: string
  repo: string
  issueNumber: number
  operation: RemoveProjectItemFieldValueOperation
}): Promise<ClearProjectItemFieldValueMutationMutation> {
  const query = new URLSearchParams({queryId})
  const response = await reactFetchJSON(
    `/${owner}/${repo}/issues/${issueNumber}/project_item/field_value.json?${query}`,
    {
      method: 'DELETE',
      body: {itemId: operation.id, fieldId: operation.key},
    },
  )
  const result = (await response.json()) as {
    data?: ClearProjectItemFieldValueMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message
  const data = result.data
  const projectItem = data?.clearProjectV2ItemFieldValue?.projectV2Item
  const unclearedValue = projectItemFieldValueFromResponse(projectItem?.fieldValues, operation.key)

  if (!response.ok || !data || projectItem?.id !== operation.id || unclearedValue) {
    throw new Error(error ?? 'Failed to clear project item field value')
  }

  return data
}

export function useClearProjectItemFieldValueMutation({
  owner,
  repo,
  issueNumber,
  projectItemId,
  queryKey,
  additionalQueryKeys,
  relayFieldName,
  onCompleted,
  onError,
}: {
  owner: string
  repo: string
  issueNumber: number
  projectItemId: string
  queryKey: QueryKey
  additionalQueryKeys?: readonly QueryKey[]
  relayFieldName?: string
  onCompleted?: (response: ClearProjectItemFieldValueMutationMutation) => void
  onError?: (error: Error) => void
}) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const {mutate} = useConnectionMutation<ClearProjectItemFieldValueMutationMutation, Error, MutationContext>({
    ownerId: ENTITY_FIELDS_OWNER,
    connectionPath: DOCUMENT_FIELDS_PATH,
    mutationFn: operation =>
      clearProjectItemFieldValueRequest({
        owner,
        repo,
        issueNumber,
        operation: operation as RemoveProjectItemFieldValueOperation,
      }),
    // RELAY-CLEANUP(ProjectsSectionQuery): mirror Status until the collapsed project row reads TanStack data.
    onMutate: () => ({
      relayUpdate: relayFieldName
        ? environment.applyUpdate({
            storeUpdater: store => clearRelayFieldValue(store, projectItemId, relayFieldName),
          })
        : undefined,
    }),
    onSuccess: async (data, {operation}, context) => {
      try {
        if (relayFieldName) {
          commitLocalUpdate(environment, store => clearRelayFieldValue(store, projectItemId, relayFieldName))
        }
        await settleConfirmedOperation({
          queryClient,
          targets: [
            {queryKey},
            ...(additionalQueryKeys?.map(additionalQueryKey => ({queryKey: additionalQueryKey})) ?? []),
          ],
          ownerId: ENTITY_FIELDS_OWNER,
          operation,
          applyEntityFieldItem: applyProjectItemFieldValue,
        })
        onCompleted?.(data)
      } finally {
        context?.relayUpdate?.dispose()
      }
    },
    onError: (error, _variables, context) => {
      context?.relayUpdate?.dispose()
      onError?.(error)
    },
  })

  return useCallback(
    (fieldId: string) => mutate({operation: removeProjectItemFieldValue(projectItemId, fieldId)}),
    [mutate, projectItemId],
  )
}
